import { Divider, FlyerCard, HeaderFixed, StickyHeader, Translate } from '@/components';
import AuthenticationContext from '@/context/AuthenticationContext';
import { GET_ORG } from '@/graphql/queries';
import { GetOrgResponse } from '@/graphql/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import scrollToTop from '@/utils/scroolToTop';
import { useQuery } from '@apollo/client';
import { cx } from '@emotion/css';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { Button, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './OrgProfile.styles';
import LocationList from './components/LocationList/LocationList';
import OrgProfileDrawer from './components/OrgProfileDrawer/OrgProfileDrawer';
import OrgProfileHeader from './components/OrgProfileHeader/OrgProfileHeader';
import TagList from './components/TagList/TagList';

const STICK_HEADER_HEIGHT = 163;

export const OrgProfile: React.FC = () => {
  const { id } = useParams();
  const { user } = useContext(AuthenticationContext);
  const { data } = useQuery<GetOrgResponse>(GET_ORG, {
    skip: !id,
    variables: {
      orgId: id,
    },
  });
  const [showOptions, setShowOptions] = useState(false);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(STICK_HEADER_HEIGHT);
  const [stickyTop, setStickyTop] = useState(STICK_HEADER_HEIGHT);
  const { track } = useAnalytics();
  const trackId = 'org_profile_page';

  const org = data?.org;

  useEffect(() => {
    scrollToTop();
  }, []);

  if (!org) return null;

  const isSingleFlyer = org.currentProgram.length === 1;

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:show_options`,
      label: org.name,
      value: Number(org.orgId),
    });
    setShowOptions(open);
  };

  const trackButtonClick = (label: string) => {
    track({
      category: trackId,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label,
    });
  };

  const onStickyTopChanged = (stickyTop: number) => {
    setStickyTop(stickyTop);
  };

  return (
    <>
      <HeaderFixed>
        <OrgProfileHeader
          image={org.currentProgram.length > 0 ? org.currentProgram[0].frontImageUrl : ''}
          trackButtonClick={trackButtonClick}
          toggleDrawer={toggleDrawer}
        />
      </HeaderFixed>
      <StickyHeader stickyHeaderHeight={stickyHeaderHeight} onStickyTopChanged={onStickyTopChanged}>
        <div className={styles.section}>
          <div className={styles.orgNameTitle}>
            <h2 className={styles.orgName}>{org?.name}</h2>
            <Button
              variant="contained"
              className={styles.buttonFollow}
              aria-label="Follow Organization"
              onClick={() => {
                trackButtonClick('Follow');
              }}
            >
              <Translate>Follow</Translate>
            </Button>
          </div>
          <span className={styles.description}>
            <Translate>
              Develop and nurture leadership traits, sportsmanship, and team spirit in youth via
              community and sport programs.
            </Translate>
          </span>
          {org.tags.length > 0 && <TagList tags={org?.tags} />}
          {org.locations.length > 0 && <LocationList locations={org.locations} />}
          <Divider />
        </div>
        <div className={cx(styles.section, styles.infoContainer)}>
          <div className={styles.info}>
            <PhoneInTalkOutlinedIcon />
            <a
              href={`tel:${org.links.call}`}
              className={styles.infoText}
              aria-label="organization phone"
              onClick={() => {
                trackButtonClick('Call');
              }}
            >
              {org.links.call}
            </a>
          </div>
          <div className={styles.info}>
            <PublicIcon />
            <a
              href={`${org.links.website}`}
              className={styles.infoText}
              target="_blank"
              rel="noreferrer"
              aria-label="organization website"
              onClick={() => {
                trackButtonClick('Website');
              }}
            >
              {org.links.website}
            </a>
          </div>
          <div className={styles.info}>
            <LocationOnOutlinedIcon />
            <span className={styles.infoText}>
              {org.address} <br />
              <a
                href={`http://maps.google.com/?daddr=${org.mapUrl}`}
                target="_blank"
                className={styles.link}
                rel="noreferrer"
                aria-label="organization address"
                onClick={() => {
                  trackButtonClick('Address');
                }}
              >
                <Translate>View Location</Translate>
              </a>
            </span>
          </div>
        </div>
        <div className={styles.section}>
          <Divider />
          <h3 className={styles.sectionTitle} style={{ top: `${stickyTop}px` }}>
            <Translate>Current Program</Translate>
          </h3>
          <Grid container spacing={2} className={styles.flyersContainer}>
            {org.currentProgram.map(({ flyerId, title, frontImageUrl }) => (
              <Grid item xs={isSingleFlyer ? 12 : 6} key={flyerId}>
                <FlyerCard
                  id={flyerId}
                  title={title}
                  thumbnail={frontImageUrl}
                  isFavorite={false}
                  trackId={`${trackId}:current_program`}
                  trackingEventFrom=""
                />
              </Grid>
            ))}
          </Grid>
        </div>
        {org.previouslyPosted && (
          <div className={styles.section}>
            <Divider />
            <h3 className={styles.sectionTitle} style={{ top: `${stickyTop}px` }}>
              <Translate>Previously Posted</Translate>
            </h3>
            <Grid container spacing={2} className={styles.flyersContainer}>
              {org.previouslyPosted?.map(({ flyerId, frontImageUrl, title }) => (
                <Grid item xs={6} key={flyerId}>
                  <FlyerCard
                    id={flyerId}
                    title={title}
                    thumbnail={frontImageUrl}
                    isFavorite={false}
                    trackId={`${trackId}:previouslyPosted`}
                    inactive
                    trackingEventFrom=""
                  />
                </Grid>
              ))}
            </Grid>
          </div>
        )}
        <OrgProfileDrawer setShowOptions={setShowOptions} showOptions={showOptions} />
      </StickyHeader>
    </>
  );
};
